/**
 * 图片派生工具：视频抽帧 / 九宫格切割的客户端实现。
 *
 * 派生结果（抽帧/切割）经远程上传跟账号走：
 * 节点先存本地 blob 预览，上传完成后换持久网关地址。
 */
import { boardActions, getBoardState } from '../state/boardStore';
import { uploadImage, bindUploadToNode } from '../imageStorage';
import { makeNode, nid } from '../utils';
import { MAX_NODES_PER_PROJECT } from '../constants';
import type { CanvasNode } from '../types';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import toast from '../../../components/Toast';

/** 派生图片节点与源节点的间距 */
const DERIVE_GAP = 60;

/** 加载 HTMLImageElement（跨 src 类型统一入口） */
async function loadImg(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = src;
  });
  return img;
}

// ---------------- 视频抽帧 ----------------

/**
 * 从视频 URL 抽取指定时间的帧。
 * @param videoUrl 视频节点 data.videoUrl（原始引用）
 * @param seekTime 定位秒数：首帧传 0，末帧传 duration-0.05，当前帧传播放器的 currentTime
 */
export async function extractVideoFrame(videoUrl: string, seekTime: number): Promise<Blob> {
  const video = document.createElement('video');
  video.src = resolveBrowserMediaUrl(videoUrl);
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';

  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('视频加载超时')), 30_000);
    video.onloadedmetadata = () => {
      clearTimeout(timer);
      resolve();
    };
    video.onerror = () => {
      clearTimeout(timer);
      reject(new Error('视频加载失败'));
    };
  });

  const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
  const target = Math.min(Math.max(0, seekTime), Math.max(0, duration - 0.05));

  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('帧定位超时')), 15_000);
    video.onseeked = () => {
      clearTimeout(timer);
      resolve();
    };
    video.onerror = () => {
      clearTimeout(timer);
      reject(new Error('帧定位失败'));
    };
    video.currentTime = target;
  });

  if (!video.videoWidth || !video.videoHeight) {
    throw new Error('无法读取视频画面尺寸');
  }
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法创建画布上下文');
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('帧导出失败');
  return blob;
}

/**
 * 抽帧并创建图片节点（不连线：视频节点不再有任何图片类下游，
 * 抽帧产物与源视频之间无血缘边）。
 * @param kind 仅用于生成节点提示文案
 */
export async function extractFrameToImageNode(
  sourceNode: CanvasNode,
  videoUrl: string,
  seekTime: number,
  kind: 'first' | 'last' | 'current'
): Promise<void> {
  try {
    const blob = await extractVideoFrame(videoUrl, seekTime);
    // 派生图远程上传（跟账号走）：节点先渲染本地预览，上传完成后换持久网关地址
    const up = uploadImage(blob, getBoardState().projectId ?? undefined);
    const label = kind === 'first' ? '首帧' : kind === 'last' ? '末帧' : '当前帧';
    const newNode = makeNode(
      'image',
      { x: sourceNode.x + sourceNode.width + DERIVE_GAP, y: sourceNode.y },
      {
        imageUrl: up.previewUrl,
        status: 'completed',
        prompt: `(视频${label})`,
      } as Record<string, unknown>
    );
    boardActions.addNode(newNode);
    bindUploadToNode(newNode.id, up);
    toast.success(`已抽取视频${label}`);
  } catch (err) {
    toast.error(err instanceof Error ? err.message : '抽帧失败');
  }
}

// ---------------- 九宫格切割 ----------------

export interface SplitResult {
  created: number;
}

/**
 * 把图片按 rows × cols 切割，创建子图片节点并以网格排布在源节点右侧，各自与源节点连线。
 */
export async function splitImageToGrid(
  sourceNode: CanvasNode,
  imageUrl: string,
  rows: number,
  cols: number
): Promise<SplitResult> {
  const img = await loadImg(resolveBrowserMediaUrl(imageUrl));
  const cellW = Math.floor(img.naturalWidth / cols);
  const cellH = Math.floor(img.naturalHeight / rows);
  if (cellW < 8 || cellH < 8) {
    throw new Error('切分后单格尺寸过小');
  }

  const total = rows * cols;
  if (getBoardState().nodes.length + total > MAX_NODES_PER_PROJECT) {
    throw new Error('节点数将超出上限，无法切割');
  }

  // 网格排布参数：子节点保持单元格宽高比，单格最大宽 220
  const nodeW = Math.min(220, cellW);
  const nodeH = Math.round((nodeW * cellH) / cellW);
  const gap = 12;
  const originX = sourceNode.x + sourceNode.width + DERIVE_GAP;
  const originY = sourceNode.y;

  const canvas = document.createElement('canvas');
  canvas.width = cellW;
  canvas.height = cellH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法创建画布上下文');

  const newNodes: CanvasNode[] = [];
  const newEdges: Array<{ id: string; sourceNodeId: string; targetNodeId: string; sourceHandle: string; targetHandle: string }> = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      ctx.clearRect(0, 0, cellW, cellH);
      ctx.drawImage(img, c * cellW, r * cellH, cellW, cellH, 0, 0, cellW, cellH);
      const dataUrl = canvas.toDataURL('image/png');
      const blob = await (await fetch(dataUrl)).blob();
      const up = uploadImage(blob, getBoardState().projectId ?? undefined);

      const node = makeNode(
        'image',
        {
          x: originX + c * (nodeW + gap),
          y: originY + r * (nodeH + gap),
        },
        {
          imageUrl: up.previewUrl,
          status: 'completed',
          prompt: `(切割 ${r + 1}-${c + 1})`,
        } as Record<string, unknown>,
        { width: nodeW, height: nodeH }
      );
      newNodes.push(node);
      bindUploadToNode(node.id, up);
      newEdges.push({
        id: nid(),
        sourceNodeId: sourceNode.id,
        targetNodeId: node.id,
        sourceHandle: 'right',
        targetHandle: 'left',
      });
    }
  }

  // 最终校验：上方预检查与本批逐格上传（秒级）之间可能新增节点；
  // addNodes 超限是静默跳过，不在此拦截会让弹窗误报成功、上传流量全废
  if (getBoardState().nodes.length + newNodes.length > MAX_NODES_PER_PROJECT) {
    throw new Error('节点数已超出上限，本次切割已取消（分块未创建）');
  }

  // 一次性入画布（单条历史）
  boardActions.addNodes(newNodes, newEdges);
  return { created: newNodes.length };
}
